import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { Subscription } from 'rxjs';

import { NoteItemForm } from '../models/note-item-form.model';

@Injectable({ providedIn: 'any' })
export class NoteFormLockingService {
  setupByTotalPriceValidation(
    group: FormGroup<NoteItemForm>,
    invoiceTotal: number,
    getPriceMode: () => 'absolute' | 'percentage',
  ): Subscription {
    return group.controls.price.valueChanges.subscribe((price) => {
      const numPrice = Number(price) || 0;

      if (numPrice < 0) {
        group.controls.price.setValue(0, { emitEvent: false });
        return;
      }

      const mode = getPriceMode();
      const max = mode === 'percentage' ? 100 : invoiceTotal;

      if (numPrice > max) {
        group.controls.price.setValue(max, { emitEvent: false });
      }
    });
  }

  setupByArticleQuantityLocking(
    group: FormGroup<NoteItemForm>,
    index: number,
    originalQty: number,
    originalPrice: number,
    getPriceMode: (index: number) => 'absolute' | 'percentage',
    onLockPrice: (
      group: FormGroup<NoteItemForm>,
      index: number,
      originalPrice: number,
    ) => void,
    onUnlockPrice: (group: FormGroup<NoteItemForm>, index: number) => void,
  ): Subscription {
    return group.controls.quantity.valueChanges.subscribe((qty) => {
      const numQty = Number(qty) || 0;

      if (numQty < 0) {
        group.controls.quantity.setValue(0, { emitEvent: false });
        return;
      }

      if (numQty > originalQty) {
        group.controls.quantity.setValue(originalQty, { emitEvent: false });
        return;
      }

      if (numQty > 0) {
        onLockPrice(group, index, originalPrice);
      } else {
        onUnlockPrice(group, index);
      }
    });
  }

  setupByArticlePriceLocking(
    group: FormGroup<NoteItemForm>,
    index: number,
    originalQty: number,
    originalPrice: number,
    getPriceMode: (index: number) => 'absolute' | 'percentage',
    isPriceLocked: (index: number) => boolean,
    onUnlockQuantityAndReset: (
      group: FormGroup<NoteItemForm>,
      index: number,
    ) => void,
    onLockQuantityByPrice: (
      group: FormGroup<NoteItemForm>,
      index: number,
      originalQty: number,
    ) => void,
  ): Subscription {
    return group.controls.price.valueChanges.subscribe((price) => {
      if (isPriceLocked(index)) return;

      const mode = getPriceMode(index);
      const numPrice = Number(price) || 0;

      if (numPrice < 0) {
        group.controls.price.setValue(0, { emitEvent: false });
        return;
      }

      if (mode === 'absolute' && numPrice > originalPrice) {
        group.controls.price.setValue(originalPrice, { emitEvent: false });
        return;
      }

      if (mode === 'percentage') {
        const clamped = Math.max(0, Math.min(100, numPrice));
        if (clamped !== numPrice) {
          group.controls.price.setValue(clamped, { emitEvent: false });
          return;
        }
      }

      const effectivePrice =
        mode === 'percentage' ? (originalPrice * numPrice) / 100 : numPrice;

      if (numPrice === 0) {
        onUnlockQuantityAndReset(group, index);
      } else if (effectivePrice <= originalPrice) {
        onLockQuantityByPrice(group, index, originalQty);
      }
    });
  }
}
